import { TFunction } from "i18next";
import {
  ColumnGroup,
  Grouping,
} from "library/greco-components/column-options/types";

/**
 * "Group by" criteria for the Offers column options. Columns carry no group
 * metadata: every group is derived from the column key (and the filter type),
 * so saved views never need to store or migrate it.
 */

type GroupableColumn = { key: string; filter?: { type: string } };

const group = (
  t: TFunction,
  key: string,
  labelKey: string,
  defaultLabel: string,
  order: number
): ColumnGroup => ({ key, label: t(labelKey, defaultLabel), order });

const categoryOf = (t: TFunction, column: GroupableColumn): ColumnGroup => {
  const { key } = column;
  if (key.startsWith("Client"))
    return group(t, "client", "qtg.columnGroup.cat.client", "Client", 2);
  if (key.startsWith("Vehicle") || key === "InsuranceObjectUsage")
    return group(t, "vehicle", "qtg.columnGroup.cat.vehicle", "Vehicle", 3);
  if (key.startsWith("Owner"))
    return group(t, "owner", "qtg.columnGroup.cat.owner", "Owner", 4);
  if (key.startsWith("InvoiceMtpl"))
    return group(t, "mtpl", "qtg.columnGroup.cat.mtpl", "MTPL", 5);
  if (key.startsWith("InvoiceCasco"))
    return group(t, "casco", "qtg.columnGroup.cat.casco", "CASCO", 6);
  if (key.startsWith("GroupInvoice"))
    return group(
      t,
      "groupInvoice",
      "qtg.columnGroup.cat.groupInvoice",
      "Group invoice",
      7
    );
  return group(t, "general", "qtg.columnGroup.cat.general", "General", 1);
};

const insuranceLineOf = (
  t: TFunction,
  column: GroupableColumn
): ColumnGroup => {
  const { key } = column;
  if (key.startsWith("InvoiceMtpl"))
    return group(t, "mtpl", "qtg.columnGroup.line.mtpl", "MTPL", 1);
  if (key.startsWith("InvoiceCasco"))
    return group(t, "casco", "qtg.columnGroup.line.casco", "CASCO", 2);
  return group(t, "general", "qtg.columnGroup.line.general", "General", 99);
};

const fieldTypeOf = (t: TFunction, column: GroupableColumn): ColumnGroup => {
  const { key } = column;
  const filterType = column.filter?.type;
  if (key === "OfferStatus")
    return group(t, "status", "qtg.columnGroup.ft.status", "Status", 1);
  if (key === "OfferedInsurers" || key.endsWith("Insurer"))
    return group(t, "insurer", "qtg.columnGroup.ft.insurer", "Insurer", 2);
  if (/(Premium|Discount|DiscountPercent)$/.test(key))
    return group(
      t,
      "premiums",
      "qtg.columnGroup.ft.premiums",
      "Premiums & discounts",
      3
    );
  if (/(IsPaid|PaidAt|DueDate|Period|Payments)$/.test(key))
    return group(t, "payment", "qtg.columnGroup.ft.payment", "Payment", 4);
  if (filterType === "boolean")
    return group(t, "flags", "qtg.columnGroup.ft.flags", "Yes/No flags", 8);
  if (filterType === "date")
    return group(t, "dates", "qtg.columnGroup.ft.dates", "Dates", 5);
  if (
    key.startsWith("Client") ||
    key.startsWith("Owner") ||
    key.includes("Payer")
  )
    return group(
      t,
      "parties",
      "qtg.columnGroup.ft.parties",
      "Parties & contacts",
      6
    );
  if (key.startsWith("Vehicle") || key === "InsuranceObjectUsage")
    return group(
      t,
      "vehicle",
      "qtg.columnGroup.ft.vehicle",
      "Vehicle details",
      7
    );
  return group(t, "other", "qtg.columnGroup.ft.other", "Other", 99);
};

export const getColumnGroupings = (t: TFunction): Grouping[] => [
  {
    key: "category",
    label: t("qtg.columnGroup.by.category", "Category"),
    groupOf: (column) => categoryOf(t, column),
  },
  {
    key: "insuranceLine",
    label: t("qtg.columnGroup.by.insuranceLine", "Insurance line"),
    groupOf: (column) => insuranceLineOf(t, column),
  },
  {
    key: "fieldType",
    label: t("qtg.columnGroup.by.fieldType", "Field type"),
    groupOf: (column) => fieldTypeOf(t, column),
  },
  {
    key: "alphabetical",
    label: t("qtg.columnGroup.by.alphabetical", "Alphabetical"),
    groupOf: () => null,
  },
];
